import { useCallback, useRef, useState } from 'react'
import * as Sentry from '@sentry/nextjs'

import { toaster } from '@/components/Toaster'

import { useClickOutside } from '@/lib/core/hooks/useClickOutside'

import { type AlertRecord, useAllAlerts } from '../../api/useAllAlerts'
import { usePatchAlert } from '../../api/useUpdateAlert'
import { ALERT_TEMPLATE_BY_KEY } from '../../constants/alertTemplates'
import { AlertTemplateKey } from '../../constants/alertContract'
import { useAlertStore } from '../../store'
import { type AlertFilters, DEFAULT_ALERT_FILTERS } from '../../types/alertFilters'
import { applyAlertFilters } from '../../types/applyAlertFilters'
import { isAlertMissingError, removeAlertById, setAlertEnabled } from '../../utils'
import { buildAlertTogglePayload } from './ManageAlertsModal.utils'

export const useManageAlertsModal = () => {
  const { isManageAlertsOpen, closeManageAlerts, openCreateAlert, openEditAlert, openDeleteAlert } =
    useAlertStore()
  const { data: alerts = [], mutate: mutateAlerts } = useAllAlerts()
  const { trigger: patchAlert } = usePatchAlert()
  const [filters, setFilters] = useState<AlertFilters>(DEFAULT_ALERT_FILTERS)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  const menuRef = useRef<HTMLDivElement>(null)

  useClickOutside([menuRef], () => setOpenMenuId(null), !!openMenuId)

  const filtered = applyAlertFilters(alerts, filters)

  const handleToggleAlert = useCallback(
    async (alert: AlertRecord, enabled: boolean) => {
      // Move the switch first. Waiting on the round trip left it sitting on the
      // old value, and hanging the update off a refetch meant a failing GET
      // silently discarded a change the backend had already accepted.
      await mutateAlerts((current) => setAlertEnabled(current, alert.id, enabled), {
        revalidate: false,
      })

      try {
        await patchAlert({
          params: { id: alert.id },
          data: buildAlertTogglePayload(alert, enabled),
        })
      } catch (error) {
        if (isAlertMissingError(error)) {
          // The list was stale. Drop the row rather than leaving one that can
          // never be saved.
          await mutateAlerts((current) => removeAlertById(current, alert.id), { revalidate: false })
          toaster({ message: 'That alert no longer exists.', type: 'error' })
          return
        }

        console.error(error)
        Sentry.captureException(error, {
          tags: { feature: 'alerts', api: 'v1/alerts/:id/' },
          extra: { alertId: alert.id, method: 'patch', enabled },
        })
        toaster({ message: 'Failed to update alert. Please try again.', type: 'error' })

        // Put the switch back, so it never claims a state the backend rejected.
        await mutateAlerts((current) => setAlertEnabled(current, alert.id, alert.enabled), {
          revalidate: false,
        })
        return
      }

      // Reconcile in the background; a failure here leaves the applied change
      // in place rather than reverting it under the user.
      void mutateAlerts().catch((error) => {
        console.error(error)
        Sentry.captureException(error, {
          tags: { feature: 'alerts', api: 'v1/alerts/' },
          extra: { alertId: alert.id, context: 'post-toggle revalidation' },
        })
      })
    },
    [mutateAlerts, patchAlert],
  )

  const handleEditAlert = useCallback(
    (alert: AlertRecord) => {
      const template = ALERT_TEMPLATE_BY_KEY[alert.templateName as AlertTemplateKey]
      if (!template) return
      setOpenMenuId(null)
      openEditAlert(alert, template)
    },
    [openEditAlert],
  )

  return {
    isManageAlertsOpen,
    closeManageAlerts,
    openCreateAlert,
    openDeleteAlert,
    filters,
    setFilters,
    openMenuId,
    setOpenMenuId,
    menuRef,
    filtered,
    handleToggleAlert,
    handleEditAlert,
  }
}
